<template>
  <v-card flat rounded="lg" class="summary-card">
    <!-- Primary Metrics Row -->
    <div class="summary-card__metrics pa-5">
      <v-row>
        <!-- Total Quote Price -->
        <v-col cols="12" sm="6" md="4">
          <div class="metric-card metric-card--primary">
            <div class="metric-card__icon-wrap metric-card__icon-wrap--blue">
              <v-icon color="white" small>mdi-receipt</v-icon>
            </div>
            <div class="metric-card__content">
              <div class="metric-card__label">Total Quote Price</div>
              <div class="metric-card__value metric-card__value--blue">
                {{ formatCurrency(calculations.total_quote_price) }}
              </div>
            </div>
          </div>
        </v-col>

        <!-- Capital Expenditure -->
        <v-col cols="12" sm="6" md="4">
          <div class="metric-card">
            <div class="metric-card__icon-wrap metric-card__icon-wrap--orange">
              <v-icon color="white" small>mdi-cash-minus</v-icon>
            </div>
            <div class="metric-card__content">
              <div class="metric-card__label">Capital Expenditure</div>
              <div class="metric-card__value">
                {{ formatCurrency(calculations.total_capital_expenditure) }}
              </div>
            </div>
          </div>
        </v-col>

        <!-- Net Profit -->
        <v-col cols="12" sm="6" md="4">
          <div
            class="metric-card"
            :class="
              calculations.net_profit >= 0
                ? 'metric-card--positive'
                : 'metric-card--negative'
            "
          >
            <div
              class="metric-card__icon-wrap"
              :class="
                calculations.net_profit >= 0
                  ? 'metric-card__icon-wrap--green'
                  : 'metric-card__icon-wrap--red'
              "
            >
              <v-icon color="white" small>
                {{
                  calculations.net_profit >= 0
                    ? "mdi-trending-up"
                    : "mdi-trending-down"
                }}
              </v-icon>
            </div>
            <div class="metric-card__content">
              <div class="metric-card__label">Net Profit</div>
              <div
                class="metric-card__value"
                :class="
                  calculations.net_profit >= 0 ? 'green--text' : 'red--text'
                "
              >
                {{ formatCurrency(calculations.net_profit) }}
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-divider></v-divider>

    <!-- Detailed Breakdown -->
    <div class="summary-card__details pa-5">
      <v-row>
        <!-- Left Column: Deductions -->
        <v-col cols="12" md="6">
          <div class="detail-section">
            <div class="detail-section__title">
              <v-icon x-small color="grey darken-1" class="mr-1"
                >mdi-minus-circle</v-icon
              >
              Deductions & Fees
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    class="add-deduction-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click="$emit('add-deduction')"
                  >
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Add Custom Deduction</span>
              </v-tooltip>
            </div>

            <!-- SOP -->
            <div class="detail-row" v-if="calculations.sop_amount > 0">
              <span class="detail-row__label">SOP Amount</span>
              <span class="detail-row__value">{{
                formatCurrency(calculations.sop_amount)
              }}</span>
            </div>

            <!-- Discount -->
            <div class="detail-row" v-if="calculations.discount_amount > 0">
              <span class="detail-row__label">
                <v-icon x-small color="pink darken-1" class="mr-1"
                  >mdi-sale</v-icon
                >
                Discount
                <span v-if="calculations.discount_type === 'percentage'">
                  ({{ calculations.discount_value || 0 }}%)
                </span>
              </span>
              <span class="detail-row__value pink--text text--darken-1"
                >- {{ formatCurrency(calculations.discount_amount) }}</span
              >
            </div>

            <!-- Government Mode Deductions -->
            <template v-if="mode === 'government'">
              <div class="detail-row">
                <span class="detail-row__label">
                  <v-icon x-small color="blue" class="mr-1"
                    >mdi-file-document</v-icon
                  >
                  BIR 2307 Withholding (6%)
                </span>
                <span class="detail-row__value">{{
                  formatCurrency(calculations.bir_2307_amount)
                }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-row__label">VAT Payable</span>
                <span class="detail-row__value">{{
                  formatCurrency(calculations.total_vat_payable)
                }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-row__label">
                  Income Tax ({{ calculations.income_tax_percentage || 0 }}%)
                </span>
                <span class="detail-row__value">{{
                  formatCurrency(calculations.income_tax_amount)
                }}</span>
              </div>
            </template>

            <!-- Walk-in Mode Deductions -->
            <template v-if="mode === 'walk_in' && calculations.is_vat_purchase">
              <div class="detail-row">
                <span class="detail-row__label">VAT (12%)</span>
                <span class="detail-row__value">{{
                  formatCurrency(calculations.vat_amount_walk_in)
                }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-row__label">
                  Income Tax ({{ calculations.income_tax_percentage || 0 }}%)
                </span>
                <span class="detail-row__value">{{
                  formatCurrency(calculations.income_tax_amount)
                }}</span>
              </div>
            </template>

            <!-- Custom Deductions -->
            <template v-if="customDeductions && customDeductions.length > 0">
              <!-- Auto-Deducted Deductions -->
              <template v-if="autoDeductDeductions.length > 0">
                <div class="custom-deductions-divider">
                  <span class="caption grey--text">
                    <v-icon x-small color="blue" class="mr-1"
                      >mdi-cash-check</v-icon
                    >
                    Auto-Deducted from Payment
                  </span>
                </div>
                <deduction-list-item
                  v-for="deduction in autoDeductDeductions"
                  :key="deduction.id"
                  :deduction="deduction"
                  @edit="$emit('edit-deduction', $event)"
                  @delete="$emit('delete-deduction', $event)"
                />
                <div
                  v-if="capexDeductions.length > 0"
                  class="detail-row detail-row--subtotal"
                >
                  <span class="detail-row__label font-weight-medium">
                    <v-icon x-small color="blue" class="mr-1">mdi-sigma</v-icon>
                    Auto-Deduct Subtotal
                  </span>
                  <span
                    class="
                      detail-row__value
                      font-weight-medium
                      blue--text
                      text--darken-1
                    "
                  >
                    {{ formatCurrency(totalAutoDeductDeductions) }}
                  </span>
                </div>
              </template>

              <!-- Capital Expenditure Deductions -->
              <template v-if="capexDeductions.length > 0">
                <div class="custom-deductions-divider">
                  <span class="caption grey--text">
                    <v-icon x-small color="orange darken-2" class="mr-1"
                      >mdi-cash-minus</v-icon
                    >
                    Capital Expenditure
                  </span>
                </div>
                <deduction-list-item
                  v-for="deduction in capexDeductions"
                  :key="deduction.id"
                  :deduction="deduction"
                  @edit="$emit('edit-deduction', $event)"
                  @delete="$emit('delete-deduction', $event)"
                />
                <div
                  v-if="autoDeductDeductions.length > 0"
                  class="
                    detail-row detail-row--subtotal detail-row--subtotal-capex
                  "
                >
                  <span class="detail-row__label font-weight-medium">
                    <v-icon x-small color="orange darken-2" class="mr-1"
                      >mdi-sigma</v-icon
                    >
                    CapEx Subtotal
                  </span>
                  <span
                    class="
                      detail-row__value
                      font-weight-medium
                      orange--text
                      text--darken-2
                    "
                  >
                    {{ formatCurrency(totalCapexDeductions) }}
                  </span>
                </div>
              </template>

              <!-- Total Custom Deductions -->
              <div class="detail-row detail-row--subtotal">
                <span class="detail-row__label font-weight-medium">
                  <v-icon x-small color="orange" class="mr-1">mdi-sigma</v-icon>
                  Total Custom Deductions
                </span>
                <span
                  class="
                    detail-row__value
                    font-weight-medium
                    orange--text
                    text--darken-2
                  "
                >
                  {{ formatCurrency(totalCustomDeductions) }}
                </span>
              </div>
            </template>

            <div v-if="!hasDeductions" class="detail-row detail-row--empty">
              <span class="grey--text caption">No deductions applied</span>
            </div>
          </div>
        </v-col>

        <!-- Right Column: Key Totals -->
        <v-col cols="12" md="6">
          <div class="detail-section">
            <div class="detail-section__title">
              <v-icon x-small color="grey darken-1" class="mr-1"
                >mdi-sigma</v-icon
              >
              Key Totals
            </div>

            <!-- Government: Cheque Amount -->
            <div
              v-if="mode === 'government'"
              class="
                detail-row detail-row--highlight detail-row--highlight-blue
              "
            >
              <span class="detail-row__label font-weight-bold">
                <v-icon x-small color="blue" class="mr-1">mdi-checkbook</v-icon>
                Cheque Amount
              </span>
              <span class="detail-row__value font-weight-bold blue--text">
                {{ formatCurrency(calculations.cheque_amount) }}
              </span>
            </div>

            <!-- Walk-in: Grand Total (if VAT) -->
            <div
              v-if="mode === 'walk_in' && calculations.is_vat_purchase"
              class="
                detail-row detail-row--highlight detail-row--highlight-blue
              "
            >
              <span class="detail-row__label font-weight-bold">
                <v-icon x-small color="blue" class="mr-1"
                  >mdi-cash-register</v-icon
                >
                Grand Total (Customer Pays)
              </span>
              <span class="detail-row__value font-weight-bold blue--text">
                {{ formatCurrency(grandTotalCustomerPays) }}
              </span>
            </div>

            <!-- Total After Discount (Customer Pays) -->
            <div
              v-if="calculations.discount_amount > 0"
              class="
                detail-row detail-row--highlight detail-row--highlight-pink
              "
            >
              <span class="detail-row__label font-weight-bold">
                <v-icon x-small color="pink darken-1" class="mr-1"
                  >mdi-cash-register</v-icon
                >
                Total After Discount (Customer Pays)
              </span>
              <span
                class="
                  detail-row__value
                  font-weight-bold
                  pink--text
                  text--darken-1
                "
              >
                {{ formatCurrency(calculations.customer_total_after_discount) }}
              </span>
            </div>

            <!-- Total All Deductions (shown when custom deductions exist) -->
            <div
              v-if="customDeductions && customDeductions.length > 0"
              class="
                detail-row detail-row--highlight detail-row--highlight-orange
              "
            >
              <span class="detail-row__label font-weight-bold">
                <v-icon x-small color="orange darken-2" class="mr-1"
                  >mdi-minus-circle-multiple</v-icon
                >
                Total All Deductions
              </span>
              <span
                class="
                  detail-row__value
                  font-weight-bold
                  orange--text
                  text--darken-2
                "
              >
                {{ formatCurrency(totalAllDeductions) }}
              </span>
            </div>

            <!-- Net Profit highlight -->
            <div
              class="detail-row detail-row--highlight"
              :class="
                calculations.net_profit >= 0
                  ? 'detail-row--highlight-green'
                  : 'detail-row--highlight-red'
              "
            >
              <span class="detail-row__label font-weight-bold">Net Profit</span>
              <span
                class="detail-row__value font-weight-bold"
                :class="
                  calculations.net_profit >= 0 ? 'green--text' : 'red--text'
                "
              >
                {{ formatCurrency(calculations.net_profit) }}
              </span>
            </div>

            <!-- Net Profit Margin -->
            <div class="detail-row">
              <span class="detail-row__label">Net Profit Margin</span>
              <span
                class="detail-row__value font-weight-medium"
                :class="
                  calculations.net_profit_margin >= 0
                    ? 'green--text'
                    : 'red--text'
                "
              >
                {{ formatPercent(calculations.net_profit_margin) }}
              </span>
            </div>

            <!-- Markup -->
            <div class="detail-row">
              <span class="detail-row__label">Markup</span>
              <span
                class="detail-row__value font-weight-medium"
                :class="calculations.markup >= 0 ? 'green--text' : 'red--text'"
              >
                {{ formatPercent(calculations.markup) }}
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import DeductionListItem from "./DeductionListItem.vue";

export default {
  components: {
    DeductionListItem,
  },
  props: {
    mode: {
      type: String,
      default: "walk_in",
    },
    calculations: {
      type: Object,
      default: () => ({}),
    },
    customDeductions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    grandTotalCustomerPays() {
      const price = parseFloat(this.calculations.total_quote_price) || 0;
      const vat = parseFloat(this.calculations.vat_amount_walk_in) || 0;
      return price + vat;
    },
    autoDeductDeductions() {
      if (!this.customDeductions) return [];
      return this.customDeductions.filter(
        (d) => d.deduction_mode !== "capital_expenditure"
      );
    },
    capexDeductions() {
      if (!this.customDeductions) return [];
      return this.customDeductions.filter(
        (d) => d.deduction_mode === "capital_expenditure"
      );
    },
    totalAutoDeductDeductions() {
      return this.autoDeductDeductions.reduce((sum, ded) => {
        return sum + (parseFloat(ded.computed_amount) || 0);
      }, 0);
    },
    totalCapexDeductions() {
      return this.capexDeductions.reduce((sum, ded) => {
        return sum + (parseFloat(ded.computed_amount) || 0);
      }, 0);
    },
    totalCustomDeductions() {
      if (!this.customDeductions || this.customDeductions.length === 0) {
        return 0;
      }
      return this.customDeductions.reduce((sum, ded) => {
        return sum + (parseFloat(ded.computed_amount) || 0);
      }, 0);
    },
    totalAllDeductions() {
      let total = 0;

      // SOP
      total += parseFloat(this.calculations.sop_amount) || 0;

      // Government mode deductions
      if (this.mode === "government") {
        total += parseFloat(this.calculations.total_vat_payable) || 0;
        total += parseFloat(this.calculations.income_tax_amount) || 0;
      }

      // Walk-in mode deductions (if VAT purchase)
      if (this.mode === "walk_in" && this.calculations.is_vat_purchase) {
        total += parseFloat(this.calculations.vat_amount_walk_in) || 0;
        total += parseFloat(this.calculations.income_tax_amount) || 0;
      }

      // Only auto-deducted custom deductions count here
      // (capex customs are already included in Capital Expenditure)
      total += this.totalAutoDeductDeductions;

      // Discount comes straight off the top
      total += parseFloat(this.calculations.discount_amount) || 0;

      return total;
    },
    hasDeductions() {
      if (this.calculations.sop_amount > 0) return true;
      if (this.calculations.discount_amount > 0) return true;
      if (this.mode === "government") return true;
      if (this.mode === "walk_in" && this.calculations.is_vat_purchase)
        return true;
      if (this.customDeductions && this.customDeductions.length > 0)
        return true;
      return false;
    },
  },
  methods: {
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    formatPercent(val) {
      const num = parseFloat(val) || 0;
      return num.toFixed(2) + "%";
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-card {
  border: 1px solid #e8eaed;
  overflow: hidden;

  &__metrics {
    background: #fafbfc;
  }
}

/* Metric Card (top row) */
.metric-card {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e8eaed;
  background: #fff;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &--primary {
    border-color: #bbdefb;
    background: linear-gradient(135deg, #e3f2fd 0%, #fff 100%);
  }

  &--positive {
    border-color: #c8e6c9;
    background: linear-gradient(135deg, #e8f5e9 0%, #fff 100%);
  }

  &--negative {
    border-color: #ffcdd2;
    background: linear-gradient(135deg, #ffebee 0%, #fff 100%);
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    margin-right: 12px;
    flex-shrink: 0;

    &--blue {
      background: #1976d2;
    }
    &--orange {
      background: #f57c00;
    }
    &--green {
      background: #388e3c;
    }
    &--red {
      background: #d32f2f;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    color: #5f6368;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 2px;
  }

  &__value {
    font-size: 18px;
    font-weight: 700;
    color: #202124;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--blue {
      color: #1976d2;
    }
  }
}

/* Detail Section */
.detail-section {
  &__title {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: #5f6368;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }
}

/* Add Deduction Button */
.add-deduction-btn {
  margin-left: 8px;

  &:hover {
    background-color: rgba(25, 118, 210, 0.1);
  }
}

/* Custom Deductions Divider */
.custom-deductions-divider {
  display: flex;
  align-items: center;
  margin: 12px 0 8px;
  padding: 0 12px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e8eaed;
  }

  span {
    padding: 0 12px;
    color: #9e9e9e;
  }
}

/* Detail Row */
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &__label {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #3c4043;
  }

  &__value {
    font-size: 13px;
    color: #202124;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  &--empty {
    justify-content: center;
    padding: 16px;
  }

  &--subtotal {
    background: #fff8e1;
    border: 1px dashed #ffcc80;
    margin-top: 8px;

    &-capex {
      background: #fff3e0;
      border-color: #ffb74d;
    }
  }

  &--highlight {
    padding: 10px 12px;
    margin-bottom: 6px;
    border-radius: 8px;

    &-blue {
      background: #e3f2fd;
      border: 1px solid #bbdefb;
    }
    &-green {
      background: #e8f5e9;
      border: 1px solid #c8e6c9;
    }
    &-red {
      background: #ffebee;
      border: 1px solid #ffcdd2;
    }
    &-orange {
      background: #fff3e0;
      border: 1px solid #ffcc80;
    }
    &-pink {
      background: #fce4ec;
      border: 1px solid #f8bbd0;
    }
  }
}
</style>
